import React, { useState } from 'react';
import { Team } from '../../types';
import { TeamCard } from './TeamCard';
import { Button } from '../UI/Button';
import styles from './TeamDisplay.module.css';

export interface TeamDisplayProps {
  teams: Team[];
  onRegenerateTeams?: () => Promise<void>;
  isLoading?: boolean;
}

/**
 * TeamDisplay component shows all teams and allows regeneration
 * 
 * Features:
 * - Renders TeamCard for each of 4 teams
 * - Responsive grid layout (2x2 on desktop, stack on mobile)
 * - "Randomize Teams" button with confirmation dialog
 * - Loading state during generation
 */
export const TeamDisplay: React.FC<TeamDisplayProps> = ({ 
  teams, 
  onRegenerateTeams,
  isLoading = false 
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerateClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmRegenerate = async () => {
    if (!onRegenerateTeams) return;

    setShowConfirmation(false);
    setIsRegenerating(true);

    try {
      await onRegenerateTeams();
    } catch (err) {
      console.error('Failed to regenerate teams:', err);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCancelRegenerate = () => {
    setShowConfirmation(false);
  };

  const showLoading = isLoading || isRegenerating;

  return (
    <div className={styles.teamDisplay}>
      <div className={styles.header}>
        <h2 className={styles.title}>Team Assignments</h2>
        {onRegenerateTeams && (
          <Button
            variant="primary"
            onClick={handleRegenerateClick}
            disabled={showLoading}
            className={styles.regenerateButton}
          >
            {showLoading ? 'Generating...' : 'Randomize Teams'}
          </Button>
        )}
      </div>

      {showConfirmation && (
        <div className={styles.confirmationDialog}>
          <div className={styles.dialogContent}>
            <p className={styles.dialogMessage}>
              Are you sure you want to regenerate teams? This will create new team assignments.
            </p>
            <div className={styles.dialogActions}>
              <Button variant="default" onClick={handleCancelRegenerate}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmRegenerate}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.teamsGrid}>
        {teams.map((team) => (
          <TeamCard key={team.id} team={team} />
        ))}
      </div>
    </div>
  );
};
